import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Coins, FileEdit, FileText, Plus, Sparkles, TrendingUp, Wallet, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { commissionsApi, type ClaimStatus, type CommissionClaimSummaryDto } from "@/api/commissions";
import { extractErrorMessage } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { useAuth } from "@/hooks/useAuth";
import { Button, Card } from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { ALL_CLAIM_STATUSES, ClaimStatusBadge } from "@/components/commissions/ClaimStatusBadge";
import { currencyCompact, currency, percent, shortDate, truncate } from "@/components/commissions/formatters";
import { useDateFormatter } from "@/utils/datetime";

// DERIVED from the one canonical list, so this row and the All Claims row can
// never again offer different sets of statuses. Ordering follows the C# enum's
// ordinals, which swaps TWO chips against the hand-written version it replaces:
// `Withdrawn` moves from last to seventh and `Suggested` from seventh to last.
// Membership is unchanged — that list already carried all eleven.
const ALL_STATUSES: Array<ClaimStatus | "All"> = ["All", ...ALL_CLAIM_STATUSES];

export function MyClaimsDashboard() {
  const { tz } = useDateFormatter();
  const year = new Date().getFullYear();
  const { hasPermission } = useAuth();
  const canManualCreate = hasPermission("Commission.ManualCreate");
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | "All">("All");

  const stats = useQuery({
    queryKey: commissionKeys.mineStats(year),
    queryFn: () => commissionsApi.myStats(year),
  });

  const claims = useQuery({
    queryKey: commissionKeys.mineClaims(statusFilter),
    queryFn: () =>
      commissionsApi.listClaims({
        onlyMine: true,
        pageSize: 100,
        status: statusFilter === "All" ? undefined : statusFilter,
      }),
  });

  const contestedCount = stats.data?.contestedCount ?? 0;

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Commissions
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            My Claims
          </h1>
          <p className="text-body text-gray-mid mt-space-2 max-w-reading">
            Track what you've logged, what Accounting is reviewing, and what's been paid.
          </p>
        </div>
        {canManualCreate && (
          <div className="flex gap-space-2">
            <Link to="/commissions/templates">
              <Button variant="tertiary">Templates</Button>
            </Link>
            <Link to="/commissions/bulk">
              <Button variant="secondary">Bulk entry</Button>
            </Link>
            <Link to="/commissions/new">
              <Button variant="primary">
                <Plus size={14} strokeWidth={2} aria-hidden /> New claim
              </Button>
            </Link>
          </div>
        )}
      </div>

      <SuggestedClaimsSection />

      {contestedCount > 0 && (
        <div className="flex items-start gap-space-3 p-space-4 border border-status-warning rounded-md bg-status-warning/5">
          <AlertTriangle size={20} strokeWidth={1.5} className="text-status-warning shrink-0 mt-[2px]" aria-hidden />
          <div>
            <div className="font-display font-bold text-body text-onyx">
              You have {contestedCount}{" "}
              {contestedCount === 1 ? "claim that needs" : "claims that need"} your attention
            </div>
            <div className="text-small text-gray-mid mt-space-1">
              Accounting has questions. Open the claim, review the contest notes, and revise, affirm or withdraw.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-space-3">
        <StatCard
          label="YTD Earned"
          value={currencyCompact(stats.data?.ytdEarned)}
          icon={TrendingUp}
          featured
        />
        <StatCard
          label="Pending Payment"
          value={currencyCompact(stats.data?.pendingPayment)}
          icon={Wallet}
        />
        <StatCard
          label="Awaiting Revenue"
          value={currencyCompact(stats.data?.awaitingRevenue)}
          icon={Coins}
        />
        <StatCard
          label="In Negotiation"
          value={currencyCompact(stats.data?.inNegotiation)}
          icon={FileText}
        />
        <StatCard
          label="Drafts"
          value={String(stats.data?.draftCount ?? 0)}
          subValue={
            stats.data?.draftCount
              ? currencyCompact(stats.data.draftTotal)
              : undefined
          }
          icon={FileEdit}
          onClick={() => setStatusFilter("Draft")}
        />
      </div>

      <div className="flex flex-wrap gap-space-2 items-center">
        <span className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          Status
        </span>
        {ALL_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={
              "px-space-3 h-8 rounded-sm font-display text-label font-semibold uppercase tracking-button " +
              "border transition-colors duration-fast " +
              (statusFilter === s
                ? "bg-gold text-onyx border-gold"
                : "bg-white-pure text-gray-mid border-border-light hover:border-gold")
            }
          >
            {s === "All" ? "All" : s}
          </button>
        ))}
      </div>

      <Card className="p-0">
        {claims.isLoading ? (
          <div className="px-space-4 py-space-6 font-display text-label uppercase tracking-label text-gray-mid">
            Loading claims…
          </div>
        ) : !claims.data || claims.data.data.length === 0 ? (
          <div className="px-space-4 py-space-6 text-center">
            <div className="font-display font-bold text-body text-onyx">No claims yet.</div>
            <div className="text-small text-gray-mid mt-space-1">
              {canManualCreate
                ? "Start by logging a claim against a revenue pool."
                : "When servicing income is recognized, suggested claims appear above for you to accept."}
            </div>
            {canManualCreate && (
              <Link to="/commissions/new" className="inline-block mt-space-3">
                <Button variant="primary">
                  <Plus size={14} strokeWidth={2} aria-hidden /> New claim
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-small">
              <thead className="bg-onyx">
                <tr>
                  <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Claim #
                  </th>
                  <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Pool
                  </th>
                  <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Description
                  </th>
                  <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Gross
                  </th>
                  <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    %
                  </th>
                  <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Commission
                  </th>
                  <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Status
                  </th>
                  <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Round
                  </th>
                  <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {claims.data.data.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-border-light hover:bg-gold-highlight/10 transition-colors duration-fast"
                  >
                    <td className="px-space-3 py-space-3 font-mono text-small">
                      <Link to={`/commissions/${c.id}`} className="text-gold-shadow hover:underline">
                        {c.claimNumber}
                      </Link>
                    </td>
                    <td className="px-space-3 py-space-3">
                      <span className="font-display text-label font-semibold tracking-label text-gray-mid">
                        {c.poolCode}
                      </span>{" "}
                      <span className="text-onyx">{c.poolName}</span>
                    </td>
                    <td className="px-space-3 py-space-3 text-gray-mid">
                      {truncate(c.serviceDescription, 60)}
                    </td>
                    <td className="px-space-3 py-space-3 text-right tabular-nums">
                      {currency(c.grossRevenue)}
                    </td>
                    <td className="px-space-3 py-space-3 text-right tabular-nums">
                      {percent(c.commissionPercent)}
                    </td>
                    <td className="px-space-3 py-space-3 text-right tabular-nums font-semibold">
                      {currency(c.commissionAmount)}
                    </td>
                    <td className="px-space-3 py-space-3">
                      <ClaimStatusBadge status={c.status} />
                    </td>
                    <td className="px-space-3 py-space-3 text-right tabular-nums">
                      {c.reviewRound > 0 ? c.reviewRound : "—"}
                    </td>
                    <td className="px-space-3 py-space-3 text-gray-mid">{shortDate(c.createdAt, tz)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SuggestedClaimsSection() {
  const qc = useQueryClient();
  const suggested = useQuery({
    queryKey: commissionKeys.suggested(),
    queryFn: () => commissionsApi.mySuggested(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: commissionKeys.all });

  // 🔴 BOTH MUTATIONS REPORT THEIR FAILURES, AND BEFORE THIS FILE HAD NOWHERE
  // TO PUT ONE. There was no `onError`, no `setError` and no `catch` anywhere in
  // it: `.mutate()` captures the rejection into mutation state and offers it to
  // `onError`, and with nothing reading `error`/`isError`/`onError` a 400 simply
  // ended there. Observed: a claimant clicked Accept on a claim in a LOCKED
  // period, the network log showed two `POST .../accept` → 400, and the screen
  // did not change. This is not period-lock-specific — every 400 these two can
  // produce (wrong status, a concurrent change, the period lock) vanished the
  // same way, and `decline` routes to `withdraw`, which is TERMINAL and forfeits
  // the money.
  //
  // `extractErrorMessage` rather than the inline `e instanceof ApiError ?
  // e.errors[0] ...` the claim detail page uses: `api/client.ts` states in terms
  // that every user-facing message must route through it, and it additionally
  // filters blank entries and cannot degrade to "Error: [object Object]" if
  // something that is not an ApiError ever arrives. The sibling predates the
  // helper; this does not copy its shape.
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: (id: string) => commissionsApi.accept(id),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });
  const decline = useMutation({
    mutationFn: (id: string) => commissionsApi.withdraw(id, "Declined suggested claim"),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  // Declining routes through the same terminal Withdrawn state the claim detail
  // page's Withdraw does, but here it is an icon-only ✕ sitting immediately
  // beside Accept in a list row — the highest-misclick shape in the module for
  // an action that cannot be undone and forfeits the money. Same guard.
  const [pendingDecline, setPendingDecline] = useState<CommissionClaimSummaryDto | null>(null);

  const rows = suggested.data ?? [];
  if (rows.length === 0) return null;

  const pending = accept.isPending || decline.isPending;

  return (
    <>
    <Card className="p-0 border-gold">
      <div className="flex items-center gap-space-2 px-space-4 py-space-3 border-b border-border-light">
        <Sparkles size={18} strokeWidth={1.5} className="text-gold-shadow" aria-hidden />
        <div className="font-display font-bold text-body text-onyx">Suggested for you</div>
        <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
          {rows.length} awaiting your acceptance
        </span>
      </div>
      {/* In the CARD, never the ConfirmModal: `onConfirm` below nulls
          `pendingDecline` before it calls `decline.mutate`, so the modal is
          already unmounted when the failure arrives. A failed decline also
          never invalidates, so the row count cannot drop out from under this
          banner at the moment it is needed. */}
      {error && (
        <div
          role="alert"
          className="mx-space-4 mt-space-3 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small"
        >
          {error}
        </div>
      )}
      <ul className="flex flex-col m-0 p-0 list-none">
        {rows.map((c: CommissionClaimSummaryDto) => (
          <li
            key={c.id}
            className="flex flex-wrap items-center gap-space-3 px-space-4 py-space-3 border-b border-border-light last:border-b-0"
          >
            <div className="flex-1 min-w-[14rem]">
              <Link to={`/commissions/${c.id}`} className="font-mono text-small text-gold-shadow hover:underline">
                {c.claimNumber}
              </Link>{" "}
              <span className="font-display text-label font-semibold tracking-label text-gray-mid">
                {c.poolCode}
              </span>
              {/* SP-5 — the "why am I seeing this?" sentence: the suggestion's
                  source income, named in full (no truncation). */}
              <div className="text-small text-gray-mid mt-space-1">{c.serviceDescription}</div>
            </div>
            <div className="text-right tabular-nums">
              <div className="font-semibold text-onyx">{currency(c.commissionAmount)}</div>
              <div className="text-caption text-gray-mid">
                {percent(c.commissionPercent)} of {currency(c.grossRevenue)}
              </div>
            </div>
            <div className="flex gap-space-2">
              <Button
                variant="primary"
                size="sm"
                disabled={pending}
                onClick={() => accept.mutate(c.id)}
              >
                <Check size={14} strokeWidth={2} aria-hidden /> Accept
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pending}
                onClick={() => setPendingDecline(c)}
                aria-label="Decline"
              >
                <X size={14} strokeWidth={1.5} aria-hidden />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>

    <ConfirmModal
      open={pendingDecline !== null}
      title="Decline this suggested claim?"
      destructive
      confirmLabel="Decline claim"
      cancelLabel="Keep it"
      onCancel={() => setPendingDecline(null)}
      onConfirm={() => {
        const target = pendingDecline;
        setPendingDecline(null);
        if (target) decline.mutate(target.id);
      }}
      message={
        pendingDecline && (
          <p className="m-0">
            Declining <span className="font-mono">{pendingDecline.claimNumber}</span> for{" "}
            <span className="font-semibold tabular-nums">
              {currency(pendingDecline.commissionAmount)}
            </span>{" "}
            withdraws it for good. Withdrawn is a terminal state — the suggestion cannot be
            reinstated and no commission will be paid on it.
          </p>
        )
      }
    />
    </>
  );
}

function StatCard({
  label,
  value,
  subValue,
  icon: Icon,
  featured,
  onClick,
}: {
  label: string;
  value: string;
  subValue?: string;
  icon: LucideIcon;
  featured?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <Card featured={featured}>
      <div className="flex items-start justify-between mb-space-3">
        <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
          {label}
        </span>
        <Icon size={18} strokeWidth={1.5} className="text-gold-shadow shrink-0" aria-hidden />
      </div>
      <div className="font-display font-bold text-h2 text-onyx leading-tight tabular-nums">{value}</div>
      {subValue && (
        <div className="font-mono text-caption text-gray-mid mt-space-1 tabular-nums">{subValue}</div>
      )}
    </Card>
  );
  if (!onClick) return body;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left transition-transform duration-fast hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-gold rounded-md"
    >
      {body}
    </button>
  );
}
